import React, { useEffect, useState } from "react";
import MobileStaffLayout from "../../components/mobile-staff/MobileStaffLayout";
import api from "../../api/client";
import { fmtDate, fmtXOF } from "../../lib/format";
import { Sparkles, Phone, MessageCircle, Search, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

// V13 : même référentiel que la page Prospects (/api/prospects). La conversion en client se fait
// depuis le back-office (création du client) — elle n'est pas un simple changement de statut.
const STATUTS = [
  { v: "NOUVEAU", l: "Nouveau", cls: "bg-[#E8EBFA] text-[#1E3A8A] border-[#C5CCE8]" },
  { v: "A_CONTACTER", l: "À contacter", cls: "bg-[#FFF4E5] text-[#7A5A1A] border-[#FFE4B0]" },
  { v: "CONTACTE", l: "Contacté", cls: "bg-[#FFF4E5] text-[#7A5A1A] border-[#FFE4B0]" },
  { v: "QUALIFIE", l: "Qualifié", cls: "bg-[#E6F4FB] text-[#0B5A7A] border-[#B9DDEE]" },
  { v: "NON_QUALIFIE", l: "Non qualifié", cls: "bg-[#FBEAEA] text-[#7A2D2D] border-[#E5C8C8]" },
  { v: "CONVERTI", l: "Converti", cls: "bg-[#E5F0E8] text-[#2D5A3C] border-[#B9D8C2]" },
];

export default function StaffProspects() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("NOUVEAU");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prospects", { params: statutFilter ? { statut: statutFilter } : {} });
      setLeads(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statutFilter]); // eslint-disable-line

  const updateStatut = async (id, s) => {
    try {
      await api.patch(`/prospects/${id}`, { statut: s });
      toast.success("Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const filtered = leads.filter((l) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (l.nom || "").toLowerCase().includes(q) || (l.telephone || "").toLowerCase().includes(q) || (l.bien_ref || "").toLowerCase().includes(q);
  });

  return (
    <MobileStaffLayout title="Prospects">
      <div className="px-5 pt-5 pb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-1">// PIPELINE COMMERCIAL</div>
        <h2 className="font-display text-2xl font-extrabold text-[#0F1A3D] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Prospects crédit</h2>

        {/* Filter tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="ms-prospects-tabs">
          {STATUTS.map((s) => (
            <button
              key={s.v}
              onClick={() => setStatutFilter(s.v)}
              className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full border-2 transition ${statutFilter === s.v ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-[#E2E5F1] bg-white text-[#4B5775]"}`}
              data-testid={`ms-tab-${s.v}`}
            >
              {s.l}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mt-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3B5]" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher un prospect…" className="w-full h-10 pl-9 pr-3 text-sm rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]" data-testid="ms-prospects-search" />
        </div>
      </div>

      <div className="px-5 space-y-2.5">
        {loading ? (
          <div className="text-center py-8 text-[#4B5775]"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E2E5F1] rounded-xl p-8 text-center">
            <Sparkles className="w-8 h-8 text-[#1E3A8A] mx-auto opacity-50 mb-2" />
            <div className="text-sm font-bold text-[#0F1A3D]">Aucun prospect</div>
            <div className="text-xs text-[#4B5775] mt-1">{statutFilter === "NOUVEAU" ? "Tous les nouveaux prospects ont été traités." : "Aucun prospect dans cette catégorie."}</div>
          </div>
        ) : (
          filtered.map((l) => {
            const wa = `https://wa.me/${(l.telephone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Bonjour ${`${l.prenom || ""} ${l.nom || ""}`.trim()}, je vous contacte au sujet de votre simulation TPCO.`)}`;
            return (
              <div key={l.id} className="bg-white border border-[#E2E5F1] rounded-xl p-3.5" data-testid={`ms-lead-${l.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#E91E63] text-white flex items-center justify-center text-xs font-bold shrink-0">{(l.nom || "?").slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#0F1A3D] truncate">{`${l.prenom || ""} ${l.nom || ""}`.trim()}</div>
                    <div className="text-[11px] text-[#4B5775] font-mono">{l.telephone}</div>
                    {l.bien_ref && <div className="text-[10px] text-[#9CA3B5] truncate mt-0.5">📍 {l.bien_ref}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-[#9CA3B5] font-bold uppercase">Mensualité</div>
                    <div className="text-xs font-bold text-[#0F1A3D] mt-0.5">{fmtXOF(l.simulation?.mensualite || 0)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-[10px] text-[#4B5775] bg-[#F8FAFC] rounded-md p-2 mb-3">
                  <div><span className="block text-[9px] uppercase tracking-wider text-[#9CA3B5] font-bold">Prix</span> {fmtXOF(l.simulation?.prix_total || 0)}</div>
                  <div><span className="block text-[9px] uppercase tracking-wider text-[#9CA3B5] font-bold">Durée</span> {l.simulation?.duree_mois}m</div>
                  <div><span className="block text-[9px] uppercase tracking-wider text-[#9CA3B5] font-bold">Date</span> {fmtDate(l.created_at)}</div>
                </div>

                <div className="flex items-center gap-2">
                  <a href={`tel:${l.telephone}`} className="flex-1 h-9 rounded-md bg-[#1E3A8A] text-white text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5" data-testid={`ms-call-${l.id}`}>
                    <Phone className="w-3 h-3" /> Appeler
                  </a>
                  <a href={wa} target="_blank" rel="noreferrer" className="flex-1 h-9 rounded-md bg-[#25D366] text-white text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                    <MessageCircle className="w-3 h-3" /> WhatsApp
                  </a>
                  <select
                    value={l.statut}
                    onChange={(e) => updateStatut(l.id, e.target.value)}
                    data-testid={`ms-statut-${l.id}`}
                    className="h-9 text-[10px] uppercase tracking-wider font-bold px-2 rounded-md border border-[#E2E5F1] text-[#4B5775] bg-white focus:outline-none"
                  >
                    {STATUTS.filter((s) => s.v !== "CONVERTI" || l.statut === "CONVERTI").map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </div>
              </div>
            );
          })
        )}
      </div>
    </MobileStaffLayout>
  );
}
